/* Helpers for parsing video links and building embeds/thumbnails.
   Kinescope posters come from the public oEmbed endpoint (reflects Origin →
   client-side fetch works); YouTube posters use the well-known image path. */

export type VideoProvider = 'kinescope' | 'youtube';

export interface ParsedVideo {
  provider: VideoProvider;
  id: string;
}

/** Extract provider + video id from a pasted link. Returns null if unknown. */
export function parseVideoUrl(input: string): ParsedVideo | null {
  const raw = input.trim();
  if (!raw) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\./, '');
  const path = url.pathname;

  if (host === 'youtu.be') {
    const id = path.replace(/^\//, '').split('/')[0];
    return id ? { provider: 'youtube', id } : null;
  }

  if (host.endsWith('youtube.com')) {
    const v = url.searchParams.get('v');
    if (v) {
      return { provider: 'youtube', id: v };
    }
    const m = path.match(/\/(?:embed|shorts|v)\/([\w-]+)/);
    return m ? { provider: 'youtube', id: m[1] } : null;
  }

  if (host.endsWith('kinescope.io')) {
    const seg = path.replace(/^\/(?:embed\/)?/, '').split('/')[0];
    return seg ? { provider: 'kinescope', id: seg } : null;
  }

  return null;
}

/** Reconstruct a canonical share link from stored provider + id. */
export function buildShareUrl(provider: string, id: string): string {
  if (!id) {
    return '';
  }
  if (provider === 'kinescope') {
    return `https://kinescope.io/${id}`;
  }
  if (provider === 'youtube') {
    return `https://youtu.be/${id}`;
  }
  return id;
}

/** Player URL for the inline iframe. */
export function buildEmbedUrl(
  provider: string,
  id: string,
  autoplay = false,
): string {
  const ap = autoplay ? '?autoplay=1' : '';
  if (provider === 'kinescope') {
    return `https://kinescope.io/${id}${ap}`;
  }
  if (provider === 'youtube') {
    return `https://www.youtube.com/embed/${id}${ap}`;
  }
  return '';
}

/** Poster image for a video, or null when unavailable. Async for Kinescope. */
export async function fetchThumbnail(
  provider: string,
  id: string,
): Promise<string | null> {
  if (!id) {
    return null;
  }

  if (provider === 'youtube') {
    return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  }

  if (provider === 'kinescope') {
    try {
      const share = `https://kinescope.io/${id}`;
      const res = await fetch(
        `https://kinescope.io/oembed?url=${encodeURIComponent(share)}&format=json`,
      );
      if (!res.ok) {
        return null;
      }
      const data = (await res.json()) as { thumbnail_url?: string };
      return data.thumbnail_url ?? null;
    } catch {
      return null;
    }
  }

  return null;
}
